import { createSlice } from "@reduxjs/toolkit";
import SLOT_STATUS from "constants/slot-status";
import { shuffle } from "lodash";

const initialState = {
  selectedLevel: null,
  remainingHealth: null,
  timeIsUp: false,
  codeStatus: {},
  score: 0,
  localBest: null,
  items: [],
  codes: {},
  shuffled: [],
  slots: [],
  playCount: 0,
};

// reducer name to implement localBest logic: game

const game = createSlice({
  name: "game",
  initialState,
  reducers: {
    setSelectedLevel: (state, action) => {
      const selectedLevel = action.payload;
      if (selectedLevel == null) {
        state.selectedLevel = null;
      }

      // Default to only chapters
      else if (selectedLevel.parts == null) {
        selectedLevel.parts = {
          chapter: {
            points: 100,
            time: 30,
            hint: true,
          },
        };
      }

      state.remainingHealth = selectedLevel?.health;
      state.selectedLevel = selectedLevel;
    },
    setTimeIsUp: (state) => {
      state.timeIsUp = true;
    },
    setRemainingHealth: (state, action) => {
      const remainingHealth = action.payload;

      if (remainingHealth < 0) {
        state.remainingHealth = 0;
      }

      state.remainingHealth = remainingHealth;
    },
    setItems: (state, action) => {
      const items = action.payload;

      if (Array.isArray(items)) {
        state.items = items;
      }
    },

    init(state) {
      state.shuffled = shuffle(state.items);
      const randomItems = state.shuffled.splice(0, 8);

      state.slots = randomItems.map((item) => [
        {
          item,
          status: SLOT_STATUS.ACTIVE,
        },
      ]);
    },

    displayRandomItem: (state, action) => {
      const slotIndex = action.payload;

      if (slotIndex >= 0 && slotIndex < state.slots?.length) {
        const slotHistory = state.slots[slotIndex];
        if (Array.isArray(slotHistory)) {
          const randomItem = state.shuffled.pop();

          slotHistory.push({
            item: randomItem,
            status: Boolean(randomItem)
              ? SLOT_STATUS.ACTIVE
              : SLOT_STATUS.EMPTY,
          });
        }
      }
    },
    setLocalBest: (state, action) => {
      const localBest = action.payload;
      state.localBest = localBest;
    },
    submitCode: (state, action) => {
      const { index, code } = action.payload;
      const slotHistory = state.slots[index];
      const slot = slotHistory[slotHistory.length - 1];
      const partitionType = slot?.item?.type;

      const codeString = String(slot?.item?.code);
      const codeFirstTwo = codeString.slice(0, 2);
      const codeNextTwo = codeString.slice(0, 4);
      const codeNextFour = codeString.slice(0, 6);

      if (slot?.item?.code === code) {
        if (codeFirstTwo) state.codeStatus[codeFirstTwo] = SLOT_STATUS.SUCCESS;
        if (codeNextTwo) state.codeStatus[codeNextTwo] = SLOT_STATUS.SUCCESS;
        if (codeNextFour) state.codeStatus[codeNextFour] = SLOT_STATUS.SUCCESS;
        slot.status = SLOT_STATUS.SUCCESS;

        state.score += state.selectedLevel?.parts[partitionType]?.points;
      } else {
        if (codeFirstTwo) state.codeStatus[codeFirstTwo] = SLOT_STATUS.FAIL;
        if (codeNextTwo) state.codeStatus[codeNextTwo] = SLOT_STATUS.FAIL;
        if (codeNextFour) state.codeStatus[codeNextFour] = SLOT_STATUS.FAIL;
        slot.status = SLOT_STATUS.FAIL;

        if (state.remainingHealth > 0) {
          state.remainingHealth -= 1;
        }
      }
    },

    setSlotStatus: (state, action) => {
      const { index, status } = action.payload;
      if (index >= state?.slots?.length) {
        return;
      }

      const slotHistory = state?.slots[index];
      const slot = slotHistory[slotHistory.length - 1];
      slot.status = status;
    },

    setCodes: (state, action) => {
      const codes = action.payload;
      state.codes = codes;
    },

    resetGame: (state, action) => {
      state.remainingHealth = state.selectedLevel?.health;
      state.timeIsUp = false;
      state.localBest =
        state.localBest < state.score ? state.score : state.localBest;
      state.score = 0;
      state.playCount += 1;
      state.shuffled = shuffle(state.items);
      state.codeStatus = {};
      const randomItems = state.shuffled.splice(0, 8);

      state.slots = randomItems.map((item) => [
        {
          item,
          status: SLOT_STATUS.ACTIVE,
        },
      ]);
    },
    exitGame: (state, action) => {
      return {
        ...initialState,
        localBest: state.localBest,
      };
    },
  },
});

export const {
  setSelectedLevel,
  resetGame,
  submitCode,
  displayRandomItem,
  setItems,
  setRemainingHealth,
  setTimeIsUp,
  exitGame,
  setSlotStatus,
  init,
  setCodes,
  setLocalBest,
} = game.actions;

export default game.reducer;
